import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { fade, openClose } from '@core/animations/animations';
import {
	Encumbrance,
	EncumbranceActionType,
	EncumbranceType,
} from '@models/encumbrance';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import { MatterMortgage } from '@models/mortgage';
import { Mortgagee } from '@models/lender';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	EncumbrancesDispatch,
	MatterEncumbrancesState,
} from '@state/matter/encumbrances';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { Observable } from 'rxjs/internal/Observable';
import { filter, map, switchMap } from 'rxjs/operators';
import {
	calculateEncumbranceTotal,
	EncumbranceCalculated,
} from 'src/app/shared/services/financialTotals/financial-totals.service';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { checkForDuplicatePriority } from '@core/util/title-insurance';
import {
	getEncumbrancePriorityText,
	getEncumbranceTypeName,
} from '@state/global/tags/mappings/tags.encumbrance';
import { MatDialog } from '@angular/material/dialog';
import {
	CreateEncumbranceComponent,
	CreateEncumbranceDialogData,
} from '../../../create-encumbrance/create-encumbrance.component';
import { Subscription } from 'rxjs';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';

@Component({
	selector: 'app-encumbrance-side-form',
	templateUrl: './encumbrance-side-form.component.html',
	animations: [fade(), openClose()],
	standalone: false,
})
export class EncumbranceSideFormComponent implements OnInit, OnDestroy {
	public ICON = ICON;
	public encumbrance: Encumbrance;
	public encumbrancesTypes = EncumbranceType;
	public total: EncumbranceCalculated;
	public actionTypes = EncumbranceActionType;
	public removeLabel;
	public showPriorityWarning: string;
	public title: string;
	public encumbranceSubscription: Subscription;

	@Select(MatterEncumbrancesState.getEncumbrance)
	encumbranceFilterFn$: Observable<(id: string) => Encumbrance>;

	@Select(MatterUploadedFilesSelectors.getLatestFileByTypeAndSystemTag)
	matterFilesFilterFn$: Observable<
		(type: MatterDocumentType, tag: string) => MatterFile
	>;
	@SelectSnapshot(MatterMortgagesState.getAllMortgages)
	mortgages: MatterMortgage[];
	@SelectSnapshot(MatterEncumbrancesState.getRemain)
	encumbranceRemains: Encumbrance[];

	constructor(
		private activatedRoute: ActivatedRoute,
		private encumbranceDispatch: EncumbrancesDispatch,
		private navigationService: NavigationService,
		private matDialog: MatDialog,
		private analytics: AnalyticsService,
	) {}
	ngOnInit(): void {
		this.encumbranceSubscription = this.activatedRoute.paramMap
			.pipe(
				map((params) => params.get('encumbranceId')),
				filter((id) => !!id),
				switchMap((id) => this.encumbranceFilterFn$.pipe(map((fn) => fn(id)))),
				filter((encumbrance) => !!encumbrance),
			)
			.subscribe((encumbrance: Encumbrance) => {
				this.encumbrance = encumbrance;
				this.showPriorityWarning = checkForDuplicatePriority(
					this.mortgages,
					this.encumbranceRemains,
					this.encumbrance,
				);
				this.total = calculateEncumbranceTotal(encumbrance);
				this.title = this.getTitle(
					encumbrance.priority,
					encumbrance.financialInstitution?.name,
					encumbrance.type,
					encumbrance.action,
				);
			});
	}

	getTitle(
		priority: number,
		instName: string,
		type: EncumbranceType,
		action: EncumbranceActionType,
	): string {
		let title = '';
		let priorityText = '';
		if (
			type === EncumbranceType.MORTGAGE &&
			action !== EncumbranceActionType.BRIDGE
		) {
			priorityText = getEncumbrancePriorityText(priority);
		}
		title = `${priorityText} ${getEncumbranceTypeName(type)}`.trim();
		if (instName) {
			title = `${title} to ${instName}`;
		}

		return title;
	}

	updateMortgagee(mortgagee: Mortgagee) {
		this.encumbranceDispatch.CreateOrMergeEncumbrance(
			this.encumbrance.id,
			mortgagee,
		);
	}

	showPaymentCalculator() {
		this.navigationService.goToUrl('payment-calculator', null, {
			state: {
				encumbrance: this.encumbrance,
			},
			relativeTo: this.activatedRoute,
		});
	}

	setFulfilled(checked: boolean) {
		if (checked) {
			this.analytics.logEvent(
				AnalyticsEvent.ENCUMBRANCE_IN_PAGE_SET_FULFILLED_CHECKBOX_CLICK,
			);
		}
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'fulfilled',
			checked,
		);
	}

	setUndertaking(checked: boolean) {
		const update: Partial<Encumbrance> = {
			undertaking: checked,
		};

		if (!checked) {
			update.fulfilled = false;
			update.registeredOnDate = null;
			update.dischargedInstrumentNumber = null;
		}

		this.encumbranceDispatch.CreateOrMergeEncumbrance(
			this.encumbrance.id,
			update,
		);
	}

	setRegisteredOn(dateString: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'registeredOnDate',
			dateString,
		);
	}

	setDischargeNumber(dischargeNumber: string) {
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'dischargedInstrumentNumber',
			dischargeNumber,
		);
	}

	editDetails() {
		const data: CreateEncumbranceDialogData = {
			encumbrance: this.encumbrance,
			mode: 'edit',
		};
		this.matDialog.open(CreateEncumbranceComponent, {
			data,
		});
	}

	ngOnDestroy(): void {
		this.encumbranceSubscription?.unsubscribe();
	}
}
